import { useEffect, useRef, useState } from "react";
import { List, Tag, Progress, Grid } from "antd";
import { getPeriodCycleInfo, getPeriodProgressPercent } from "./getPeriodCycle";

import "./styles/EventList.css";
import {
  getCurrentYears,
  getDaysLeftFromGregorian,
  getDaysLeftFromHijri,
  getDaysLeftFromJalali,
  DaysLeftResult,
} from "./getDaysLeft";

const { useBreakpoint } = Grid;

interface EventItem {
  title: string;
  type: "bg" | "primary" | "success" | "upcoming" | "urgent";
  countdown: DaysLeftResult;
  color?: string;
  photo?: string;
  note?: string;
  progressPercent?: number;
  progressColor?: string; // NEW — overrides the progress bar's own color
}

const CYCLE_SECONDS = 365 * 24 * 60 * 60;

/** Converts "#RRGGBB" to "r, g, b" so it can be dropped into an rgba() box-shadow. */
const hexToRgb = (hex: string): string => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
};

const toTotalSeconds = (r: DaysLeftResult): number =>
  r.days * 86400 + r.hours * 3600 + r.minutes * 60 + r.seconds;

const getProgressPercent = (r: DaysLeftResult): number => {
  const totalSeconds = toTotalSeconds(r);
  const clamped = Math.max(0, Math.min(totalSeconds, CYCLE_SECONDS));
  const elapsed = CYCLE_SECONDS - clamped;
  return Math.round((elapsed / CYCLE_SECONDS) * 100);
};

const PERIOD_START = "2026-08-04"; // set once to the actual start date
const CYCLE_LENGTH_DAYS = 28;      // adjust to the usual cycle length
const PERIOD_DAYS = 7;

const buildEventData = (): EventItem[] => {
  const years = getCurrentYears();
  const period = getPeriodCycleInfo(PERIOD_START, CYCLE_LENGTH_DAYS, PERIOD_DAYS);

  return [
    {
      title: period.isActive
        ? `🩸 P — روز ${period.dayOfCycle} از ${PERIOD_DAYS}`
        : "🩸 P بعدی",
      countdown: period,
      type: "urgent",
      color: "#D62839",
      progressPercent: getPeriodProgressPercent(period),
      progressColor: "#D62839", // red bar
      note: period.isActive
        ? "سچصیی سانا قوربان اولسون🥺🥺🥺😭😭"
        : undefined,
    },{
      title: "💖💖💖تولد عشقمههههه💖💖💖",
      countdown: getDaysLeftFromJalali(years.jalali, 9, 17),
      type: "urgent",
      color: "#E75480",
      note: "تولدت مبارک، عشقِ من. ❤️\n" +
          "بودنت قشنگ‌ترین هدیه‌ی زندگی منه. دوستت دارم، همیشه و تا همیشه. 🎂🤍\n",
      // photo: "/photos/birthday.jpg",
    },
    {
      title: "💓❤️‍🔥♥️ سالگرد آشناییمون ❤️💕💞",
      countdown: getDaysLeftFromJalali(years.jalali, 6, 23),
      type: "upcoming",
      color: "#9B5DE5",
      note: "سالگرد آشناییمون مبارک، عشقِ من. ❤️\n" +
          "از روزی که وارد زندگیم شدی، همه‌چیز قشنگ‌تر شد. ممنونم که کنارمی؛ دوستت دارم، امروز و همیشه. 🤍\n",
    },
    {
      title: "💗💗 روز دختر ایرانی عشقمه 💗💗",
      countdown: getDaysLeftFromHijri(years.hijri, 11, 1),
      type: "primary",
      color: "#FF6F61",
      note: "روز دختر مبارک، عشقِ زیبای من. 🌸🤍\n" +
          "امیدوارم همیشه لبخند روی لبت باشه و دلت پر از آرامش و خوشبختی. دوستت دارم، همیشه. ❤️\n",
    },
    {
      title: "❤️❤️روز دختر جهانی عشقمهه ❤️❤️",
      countdown: getDaysLeftFromGregorian(`${years.gregorian}-10-11`),
      type: "success",
      color: "#2EC4B6",
      note: "روز دختر مبارک به خاص‌ترین دخترِ دنیا. 🌸\n" +
          "لبخندت قشنگ‌ترین اتفاق هر روز منه. همیشه بدرخشی و بدونی که دوستت دارم، بیشتر از هر چیزی. ❤️\n",
    },
    {
      title: "🤍🤍🤍 روز ملکمهههههه 🤍🤍🤍",
      countdown: getDaysLeftFromHijri(years.hijri, 6, 20),
      type: "bg",
      color: "#D4AF37",
      note: "روز زن مبارک، عشقِ زندگی من. ❤️\n" +
          "بودنت بزرگ‌ترین نعمت زندگی منه. آرزو می‌کنم همیشه سلامت، خوشحال و پر از لبخند باشی. دوستت دارم، تا همیشه. 🤍\n",
    },
    {
      title: "❤️‍🔥❤️‍🔥❤️‍🔥 ولنتاین عشقم ❤️‍🔥❤️‍🔥❤️‍🔥",
      countdown: getDaysLeftFromGregorian(`${years.gregorian}-02-14`),
      type: "urgent",
      color: "#C1121F",
      note: "ولنتاین مبارک، عشقِ من. ❤️🌹\n" +
          "هر روز کنار تو برای من روز عشقه. ممنونم که قشنگ‌ترین حس دنیا رو به زندگیم آوردی. عاشقتم. 🤍\n",
    },
  ];
};

const EventList: React.FC = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.sm;
  const progressWidth = isMobile ? 140 : 200;

  const [data, setData] = useState<EventItem[]>(() => buildEventData());
  // Tracks which cards are currently flipped, keyed by title (stable across re-renders).
  const [flipped, setFlipped] = useState<Set<string>>(new Set());
  // Holds the pending auto-unflip timeout for each flipped card, so re-tapping
  // (or tapping another card) doesn't leave a stray timer that flips it back early.
  const flipTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const id = setInterval(() => setData(buildEventData()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    return () => {
      flipTimers.current.forEach((timer) => clearTimeout(timer));
      flipTimers.current.clear();
    };
  }, []);

  const toggleFlip = (title: string) => {
    setFlipped((prev) => {
      const next = new Set(prev);
      const existingTimer = flipTimers.current.get(title);

      if (next.has(title)) {
        // Manually flipping back early — cancel the pending auto-flip.
        next.delete(title);
        if (existingTimer) {
          clearTimeout(existingTimer);
          flipTimers.current.delete(title);
        }
      } else {
        next.add(title);
        if (existingTimer) clearTimeout(existingTimer);
        const timer = setTimeout(() => {
          setFlipped((current) => {
            const reverted = new Set(current);
            reverted.delete(title);
            return reverted;
          });
          flipTimers.current.delete(title);
        }, 5000);
        flipTimers.current.set(title, timer);
      }

      return next;
    });
  };
  return (
    <List
      className="event-list"
      itemLayout="horizontal"
      dataSource={data}
      renderItem={(item) => {
        const isFlipped = flipped.has(item.title);
        const hasBackContent = Boolean(item.photo || item.note);

        return (
          <List.Item className={`event-list-item event-list-item--${item.type}`}>
            <div
              className={`flip-card ${isFlipped ? "flip-card--flipped" : ""} ${
                hasBackContent ? "flip-card--flippable" : ""
              }`}
              onClick={() => hasBackContent && toggleFlip(item.title)}
              role={hasBackContent ? "button" : undefined}
              tabIndex={hasBackContent ? 0 : undefined}
              onKeyDown={(e) => {
                if (hasBackContent && (e.key === "Enter" || e.key === " ")) {
                  e.preventDefault();
                  toggleFlip(item.title);
                }
              }}
            >
              <div className="flip-card-inner">
                {/* FRONT */}
                <div className="flip-card-face flip-card-face--front">
                  <List.Item.Meta
                    title={
                      <Tag
                        className={`event-tag event-tag--${item.type} glow-tag`}
                        style={
                          item.color
                            ? ({
                                color: item.color,
                                backgroundColor: `${item.color}1A`,
                                "--glow-rgb": hexToRgb(item.color),
                              } as React.CSSProperties)
                            : undefined
                        }
                      >
                        {item.title}
                      </Tag>
                    }
                    description={
                      <div className="event-progress-row">
                        <span className="event-countdown">{item.countdown.countdown}</span>
                        <Progress
                          percentPosition={{ align: "center", type: "inner" }}
                          size={[progressWidth, 12]}
                          percent={item.progressPercent ?? getProgressPercent(item.countdown)}
                          strokeColor={item.progressColor}
                          status="active"
                        />
                      </div>
                    }
                  />
                </div>

                {/* BACK */}
                {hasBackContent && (
                  <div className="flip-card-face flip-card-face--back">
                    {item.photo && (
                      <img className="flip-card-photo" src={item.photo} alt={item.title} />
                    )}
                    {item.note && <p className="flip-card-note">{item.note}</p>}
                  </div>
                )}
              </div>
            </div>
          </List.Item>
        );
      }}
    />
  );
};

export default EventList;